import React, { useEffect, useRef } from 'react';
import './HeroWipe.css';

const WAVEFORM_HEIGHTS = [8, 14, 10, 18, 7, 12, 16, 9, 14, 6, 11, 17, 8, 13, 18, 6, 14];

const HOLD_BEFORE = 1200;
const SWEEP_1 = 1000;
const PAUSE_1 = 400;
const REVEAL_AFTER = 1200;
const AFTER_HOLD = 3000;
const SWEEP_2 = 1000;
const PAUSE_2 = 400;
const BRAND_HOLD = 1200;
const REVEAL_BEFORE = 1200;

const CYCLE =
  HOLD_BEFORE + SWEEP_1 + PAUSE_1 + REVEAL_AFTER +
  AFTER_HOLD + SWEEP_2 + PAUSE_2 + BRAND_HOLD + REVEAL_BEFORE;

const P0 = HOLD_BEFORE;
const P1 = P0 + SWEEP_1;
const P2 = P1 + PAUSE_1;
const P3 = P2 + REVEAL_AFTER;
const P4 = P3 + AFTER_HOLD;
const P5 = P4 + SWEEP_2;
const P6 = P5 + PAUSE_2;
const P7 = P6 + BRAND_HOLD;

/** Fake tap on Generate shorts during PAUSE_1 (ms from P1). No pointer travel — only press/release. */
const TAP_DOWN_MS = 120;
const TAP_UP_MS = 80;
/** Shockwave expansion time (ms from P1); independent of PAUSE_1 / divider. */
const TAP_RIPPLE_DURATION = 1800;
const TAP_ECHO_DELAY_1 = 180;
const TAP_ECHO_DELAY_2 = 360;
/** Fade button + hand at start of REVEAL_AFTER (P2). */
const BUTTON_EXIT_MS = 200;
/** Hand / fake tap anchor on Generate shorts art (lower right, near bottom edge). */
const GEN_BTN_HAND_ANCHOR_X_FRAC = 0.93;
const GEN_BTN_HAND_ANCHOR_Y_FRAC = 0.88;
/** Ripple expands from visual center of button (not from hand). */
const GEN_BTN_RIPPLE_CENTER_X_FRAC = 0.5;
const GEN_BTN_RIPPLE_CENTER_Y_FRAC = 0.5;

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

const BEFORE_VIDEO_SRC = '/hero_raw.mp4';
const BRAND_LOGO_SRC = '/assets/images/headerlogo.png';
const GENERATE_SHORTS_BTN_SRC = '/generateshortsbutton.png';
const SOCIAL_BADGE_SRC = {
  facebook: '/fb_badge.png',
  instagram: '/insta_badge.png',
  youtube: '/youtube_badge.png',
};
const FINISH_VIDEO_SRCS = ['/hero_finish1.mp4', '/hero_finish2.mp4', '/hero_finish3.mp4'];
const REACTIONS = ['❤️', '👍', '😍', '😮', '😂', '🎉'];

const MAX_DPR = 2;
const FINISH_FALLBACK = ['#141018', '#101418', '#180f14'];

/** Last good video frame per element — used when readyState dips during seek/loop. */
const VIDEO_FRAME_CACHE = new WeakMap();

function blitCover(ctx, src, sw, sh, x, y, w, h, fallbackHex) {
  const scale = Math.max(w / sw, h / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  const dx = x + (w - dw) / 2;
  const dy = y + (h - dh) / 2;
  ctx.fillStyle = fallbackHex;
  ctx.fillRect(x, y, w, h);
  ctx.drawImage(src, 0, 0, sw, sh, dx, dy, dw, dh);
}

/** Phase math only — matches previous clip-path behaviour (percent of container width). */
function getWipeState(elapsed) {
  let divX = 100;
  let beforeLeftPct = 0;
  let afterLeftPct = 0;
  let brandMode = 'none';
  let reactionActive = false;

  if (elapsed < P0) {
    divX = 100;
    beforeLeftPct = 100;
  } else if (elapsed < P1) {
    const e = easeInOut((elapsed - P0) / SWEEP_1);
    divX = 100 - e * 100;
    beforeLeftPct = divX;
  } else if (elapsed < P2) {
    divX = 0;
  } else if (elapsed < P3) {
    const e = easeOut((elapsed - P2) / REVEAL_AFTER);
    divX = e * 100;
    afterLeftPct = divX;
    reactionActive = true;
  } else if (elapsed < P4) {
    divX = 100;
    afterLeftPct = 100;
    reactionActive = true;
  } else if (elapsed < P5) {
    const e = easeInOut((elapsed - P4) / SWEEP_2);
    divX = 100 - e * 100;
    afterLeftPct = divX;
    brandMode = 'right';
  } else if (elapsed < P7) {
    divX = 0;
    brandMode = 'full';
  } else {
    const e = easeOut((elapsed - P7) / REVEAL_BEFORE);
    divX = e * 100;
    beforeLeftPct = divX;
    brandMode = 'full';
  }

  return { divX, beforeLeftPct, afterLeftPct, brandMode, reactionActive };
}

/** Room for shadowBlur + shadowOffset beyond the card; intersects with wipe clip. */
const CARD_SHADOW_MARGIN = 58;

/** Narrow clip to wipe ∩ expanded card bounds so shadows aren’t killed by the vertical strip alone. */
function clipIntersectionCardShadowBand(ctx, wipeRight, cardX, cardY, cardW, cardH, viewW, viewH) {
  const sm = CARD_SHADOW_MARGIN;
  const sx = Math.max(0, cardX - sm);
  const sy = Math.max(0, cardY - sm);
  const ex = Math.min(wipeRight, cardX + cardW + sm);
  const ey = Math.min(viewH, cardY + cardH + sm);
  if (ex <= sx || ey <= sy) return false;
  ctx.beginPath();
  ctx.rect(sx, sy, ex - sx, ey - sy);
  ctx.clip();
  return true;
}

function roundedRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

function drawVideoCoverInRect(ctx, video, x, y, w, h, fallbackHex) {
  if (!video) {
    ctx.fillStyle = fallbackHex;
    ctx.fillRect(x, y, w, h);
    return;
  }
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const ready = video.readyState >= 2 && vw > 0 && vh > 0;

  if (ready) {
    let cache = VIDEO_FRAME_CACHE.get(video);
    if (!cache || cache.width !== vw || cache.height !== vh) {
      cache = document.createElement('canvas');
      cache.width = vw;
      cache.height = vh;
      VIDEO_FRAME_CACHE.set(video, cache);
    }
    cache.getContext('2d').drawImage(video, 0, 0, vw, vh);
    blitCover(ctx, cache, vw, vh, x, y, w, h, fallbackHex);
    return;
  }

  const cached = VIDEO_FRAME_CACHE.get(video);
  if (cached && cached.width > 0 && cached.height > 0) {
    blitCover(ctx, cached, cached.width, cached.height, x, y, w, h, fallbackHex);
    return;
  }

  ctx.fillStyle = fallbackHex;
  ctx.fillRect(x, y, w, h);
}

function layoutBeforeCard(W, H) {
  let cardW = Math.min(W * 0.92, W);
  let cardH = (cardW * 9) / 16;
  const maxH = H * 0.88;
  if (cardH > maxH) {
    cardH = maxH;
    cardW = (cardH * 16) / 9;
  }
  const cardX = (W - cardW) / 2;
  const cardY = (H - cardH) / 2;
  return { cardX, cardY, cardW, cardH };
}

function layoutAfterClips(W, H, s) {
  const MOBILE_W = 640;
  let sw;
  let mw;
  let gap;
  let r;

  if (W <= MOBILE_W) {
    gap = 8;
    r = 16;
    sw = Math.max(W * 0.22, 100);
    mw = Math.max(W * 0.26, 118);
  } else {
    /* Match pre–master-canvas CSS: fixed 170 / 200, not scaled by W/900 */
    sw = 170;
    mw = 200;
    gap = 14;
    r = 14;
  }

  let totalW = sw + gap + mw + gap + sw;
  const margin = 24;
  if (totalW > W - margin) {
    const fit = (W - margin) / totalW;
    sw *= fit;
    mw *= fit;
    gap *= fit;
    r = Math.max(8, r * fit);
    totalW = sw + gap + mw + gap + sw;
  }

  const startX = (W - totalW) / 2;
  const mainH = (mw * 16) / 9;
  const sideH = (sw * 16) / 9;
  const rowH = Math.max(mainH, sideH);
  const rowY = (H - rowH) / 2;

  const clips = [
    { x: startX, w: sw, h: sideH, y: rowY + (rowH - sideH) / 2, r, fb: FINISH_FALLBACK[0], views: '2M', plat: 'facebook' },
    { x: startX + sw + gap, w: mw, h: mainH, y: rowY + (rowH - mainH) / 2, r, fb: FINISH_FALLBACK[1], views: '3M', plat: 'instagram' },
    { x: startX + sw + gap + mw + gap, w: sw, h: sideH, y: rowY + (rowH - sideH) / 2, r, fb: FINISH_FALLBACK[2], views: '1M', plat: 'youtube' },
  ];
  return { clips };
}

function drawBeforeChrome(ctx, cardX, cardY, cardW, cardH, s, containerW) {
  const pad = 14 * s;
  /* Nudge duration pill + text left on narrow viewports so they stay inside the rounded frame */
  const timeNudgeX = containerW <= 640 ? 25 : 0;
  ctx.save();
  ctx.textBaseline = 'alphabetic';
  ctx.font = `${Math.max(9, 10 * s)}px Inter, sans-serif`;
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  roundedRectPath(ctx, cardX + pad, cardY + pad, 52 * s, 22 * s, 6 * s);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.font = `700 ${Math.max(9, 10 * s)}px Inter, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.fillText('BEFORE', cardX + pad + 8 * s, cardY + pad + 11 * s);
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  roundedRectPath(
    ctx,
    cardX + cardW - pad - timeNudgeX - 42 * s,
    cardY + cardH - pad - 20 * s,
    42 * s,
    18 * s,
    5 * s,
  );
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.font = `600 ${Math.max(10, 11 * s)}px Inter, sans-serif`;
  ctx.fillText('1:24:08', cardX + cardW - pad - timeNudgeX - 36 * s, cardY + cardH - pad - 7 * s);

  const textY = cardY + cardH - pad - 4;
  const wfBottomY = textY - 14;
  const barW = Math.max(3, 4 * s);
  const barGap = Math.max(2, 3 * s);
  let bx = cardX + pad;
  WAVEFORM_HEIGHTS.forEach((h) => {
    const barH = Math.max(4, h * s);
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    roundedRectPath(ctx, bx, wfBottomY - barH, barW, barH, 2);
    ctx.fill();
    bx += barW + barGap;
  });

  ctx.font = `600 ${Math.max(10, 12 * s)}px Inter, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = 3 * s;
  ctx.fillText('Raw footage', cardX + pad, textY);
  ctx.shadowBlur = 0;

  ctx.restore();
}

function drawClipBadge(ctx, cx, cy, cw, ch, s, views, plat, socialBadges) {
  const pad = 10 * s;
  const viewsShiftX = Math.max(2, 3 * s);
  const viewsNumX = cx + pad + viewsShiftX;
  const viewsNumY = cy + ch - pad - 4;
  const viewsLblY = viewsNumY - 13;
  ctx.save();
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 ${Math.max(7, 8 * s)}px Inter, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.fillText('VIEWS', cx + pad, viewsLblY);
  const g2 = ctx.createLinearGradient(viewsNumX, cy + ch, viewsNumX + 40 * s, cy + ch);
  g2.addColorStop(0, '#a0b0ff');
  g2.addColorStop(1, '#ffb3e6');
  ctx.fillStyle = g2;
  ctx.font = `900 ${Math.max(13, 17 * s)}px Montserrat, Inter, sans-serif`;
  ctx.fillText(views, viewsNumX, viewsNumY);

  const img = socialBadges && socialBadges[plat];
  const badgeMax = Math.max(22, 28 * s);
  if (img && img.complete && img.naturalWidth > 0) {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const scale = badgeMax / Math.max(iw, ih);
    const dw = iw * scale;
    const dh = ih * scale;
    /* Sit on the clip corner: nudge past top-right so the badge rides the frame edge */
    const edge = Math.max(6, 9 * s);
    const bx = cx + cw - dw + edge * 0.5;
    const by = cy - edge * 0.45;
    ctx.drawImage(img, 0, 0, iw, ih, bx, by, dw, dh);
  }

  ctx.restore();
}

function getGenerateShortsButtonLayout(btnImg, W, H) {
  if (!btnImg || !btnImg.complete || btnImg.naturalWidth < 1) return null;
  const iw = btnImg.naturalWidth;
  const ih = btnImg.naturalHeight;
  const narrow = W <= 640;
  const maxW = Math.min(W * (narrow ? 0.78 : 0.94), narrow ? 248 : 440);
  const maxH = H * (narrow ? 0.42 : 0.58);
  const sc = Math.min(maxW / iw, maxH / ih, 1);
  const dw = iw * sc;
  const dh = ih * sc;
  const bx = (W - dw) / 2;
  const by = (H - dh) / 2;
  return { bx, by, dw, dh, iw, ih };
}

/**
 * Fixed center; no opacity animation. Visible only in x >= clipFromLeft (strip to the right of
 * the divider edge). SWEEP_1: clipFromLeft = beforeClipW (reveals as raw wipes). Pause: 0.
 * REVEAL_AFTER: clipFromLeft = splitPx (divider moving right clips the button away). elapsed >= P3: off.
 * buttonScale: 1 = normal; <1 simulates press during fake cursor click.
 * opacity: 1 = normal; <1 for exit fade after P2.
 */
function drawGenerateShortsButton(ctx, btnImg, W, H, clipFromLeft, buttonScale = 1, opacity = 1) {
  const lay = getGenerateShortsButtonLayout(btnImg, W, H);
  if (!lay) return;
  const { bx, by, dw, dh, iw, ih } = lay;

  ctx.save();
  if (opacity < 0.999) ctx.globalAlpha *= opacity;
  if (clipFromLeft > 0.5) {
    const sw = W - clipFromLeft;
    if (sw < 1) {
      ctx.restore();
      return;
    }
    ctx.beginPath();
    ctx.rect(clipFromLeft, 0, sw, H);
    ctx.clip();
  }
  if (buttonScale !== 1) {
    const cx = bx + dw / 2;
    const cy = by + dh / 2;
    ctx.translate(cx, cy);
    ctx.scale(buttonScale, buttonScale);
    ctx.translate(-cx, -cy);
  }
  ctx.drawImage(btnImg, 0, 0, iw, ih, bx, by, dw, dh);
  ctx.restore();
}

const HAND_POINTER_EMOJI = '👆';

/**
 * Hand emoji over tap target; press nudges down; reveal fades/scales in.
 * 👆 is shifted so the fingertip reads near (tx, ty) on the button.
 */
function drawHandCursor(ctx, tx, ty, pixelScale, press, reveal) {
  const r = Math.max(0, Math.min(1, reveal == null ? 1 : reveal));
  if (r < 0.02) return;
  const pop = 0.88 + 0.12 * r;
  const fontPx = Math.round(Math.max(30, Math.min(64, 40 * pixelScale * pop)));
  const p = (press || 0) * fontPx * 0.18;
  ctx.save();
  ctx.globalAlpha *= r;
  ctx.font = `${fontPx}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.22)';
  ctx.shadowBlur = Math.max(3, fontPx * 0.08);
  ctx.shadowOffsetY = Math.max(1, fontPx * 0.04);
  ctx.translate(tx, ty + p - fontPx * 0.2);
  ctx.fillText(HAND_POINTER_EMOJI, 0, 0);
  ctx.restore();
}

/** How much of the button strip is visible for x >= clipFromLeft (same as drawGenerateShortsButton). */
function buttonVisibleFraction(lay, clipFromLeft) {
  if (!lay) return 0;
  const { bx, dw } = lay;
  const visibleLeft = Math.max(bx, clipFromLeft);
  const visibleRight = bx + dw;
  return Math.max(0, Math.min(1, (visibleRight - visibleLeft) / dw));
}

/**
 * Hand on Generate shorts: reveals with SWEEP_1 clip; tap during PAUSE_1; fade out after P2.
 */
function getGenerateShortsHandOverlay(btnImg, W, H, elapsed, clipFromLeft) {
  if (elapsed < P0) return null;
  const lay = getGenerateShortsButtonLayout(btnImg, W, H);
  if (!lay) return null;
  const { bx, by, dw, dh } = lay;
  const tipX = bx + dw * GEN_BTN_HAND_ANCHOR_X_FRAC;
  const tipY = by + dh * GEN_BTN_HAND_ANCHOR_Y_FRAC;
  const cursorScale = Math.max(1.28, Math.min(2.05, (W / 900) * 1.72));

  if (elapsed >= P2) {
    const exitT = (elapsed - P2) / BUTTON_EXIT_MS;
    if (exitT >= 1) return null;
    return {
      tipX,
      tipY,
      cursorScale,
      press01: 0,
      buttonScale: 1,
      reveal01: 1 - easeInOut(exitT),
    };
  }

  let press01 = 0;
  let buttonScale = 1;
  let reveal01 = 1;

  if (elapsed < P1) {
    const sweepProgress = (elapsed - P0) / SWEEP_1;
    const frac = buttonVisibleFraction(lay, clipFromLeft);
    reveal01 = easeOut(Math.min(1, Math.min(frac, sweepProgress) * 1.4));
  } else {
    const t = elapsed - P1;
    const tapEnd = TAP_DOWN_MS + TAP_UP_MS;
    if (t < TAP_DOWN_MS) {
      press01 = easeInOut(t / TAP_DOWN_MS);
    } else if (t < tapEnd) {
      press01 = 1 - easeInOut((t - TAP_DOWN_MS) / TAP_UP_MS);
    }
    buttonScale = 1 - 0.045 * easeInOut(press01);
  }

  return { tipX, tipY, cursorScale, press01, buttonScale, reveal01 };
}

function drawBrand(ctx, W, H, logo, s) {
  const tag = 'Possible with';
  ctx.font = `600 ${Math.max(13, Math.min(18, 14 + W * 0.02))}px Montserrat, Inter, sans-serif`;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.textAlign = 'center';
  const y0 = H / 2 - 28 * s;
  ctx.fillText(tag, W / 2, y0);
  if (logo && logo.complete && logo.naturalWidth) {
    const maxW = Math.min(280, W * 0.72);
    const maxH = Math.max(40, Math.min(56, H * 0.1));
    let lw = logo.naturalWidth;
    let lh = logo.naturalHeight;
    const scale = Math.min(maxW / lw, maxH / lh, 1);
    lw *= scale;
    lh *= scale;
    ctx.drawImage(logo, (W - lw) / 2, y0 + 8 * s, lw, lh);
  }
  ctx.textAlign = 'left';
}

/**
 * Full-screen shockwave ring from (cx, cy); u = 0..1 expansion progress.
 * strength scales opacity (primary vs echoes). No time-based alpha fade — wave travels.
 */
function drawTapRippleBackground(ctx, cx, cy, u, W, H, strength = 1) {
  const maxR =
    Math.sqrt(Math.max(cx, W - cx) ** 2 + Math.max(cy, H - cy) ** 2) + 20;

  const ease = easeOut(Math.min(1, Math.max(0, u)));
  const outerR = maxR * ease;
  if (outerR < 2) return;

  const band = Math.max(6, 80 * (1 - ease));
  const innerR = Math.max(0, outerR - band);

  const alpha = 0.45 * strength;
  if (alpha < 0.01) return;

  ctx.save();
  ctx.globalAlpha *= alpha;

  ctx.beginPath();
  ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
  if (innerR > 0) {
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
  }
  ctx.clip();

  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, '#5170ff');
  g.addColorStop(0.5, '#ff66c4');
  g.addColorStop(1, '#5170ff');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  ctx.restore();
}

function drawDivider(ctx, splitPx, H) {
  const w = 5;
  const x = splitPx - w / 2;
  /* Matches original CSS: linear-gradient(to bottom, …) + layered box-shadows */
  const grd = ctx.createLinearGradient(x, 0, x, H);
  grd.addColorStop(0, '#5170ff');
  grd.addColorStop(0.5, '#ff66c4');
  grd.addColorStop(1, '#5170ff');

  ctx.save();
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  ctx.shadowColor = 'rgba(81, 112, 255, 0.8)';
  ctx.shadowBlur = 12;
  ctx.fillStyle = grd;
  ctx.fillRect(x, 0, w, H);

  ctx.shadowColor = 'rgba(255, 102, 196, 0.5)';
  ctx.shadowBlur = 24;
  ctx.fillRect(x, 0, w, H);

  ctx.shadowColor = '#fff';
  ctx.shadowBlur = 2;
  ctx.fillRect(x, 0, w, H);
  ctx.restore();
}

export default function HeroWipe() {
  const containerRef = useRef(null);
  const masterCanvasRef = useRef(null);
  const beforeVideoRef = useRef(null);
  const finishVideoRefs = [useRef(null), useRef(null), useRef(null)];
  const reactionsArrRef = useRef([]);
  const reactionActiveRef = useRef(false);
  const afterClipFracRef = useRef(0);
  const logoRef = useRef(null);
  const socialBadgesRef = useRef(null);
  const generateShortsBtnRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.src = BRAND_LOGO_SRC;
    logoRef.current = img;
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = GENERATE_SHORTS_BTN_SRC;
    generateShortsBtnRef.current = img;
  }, []);

  useEffect(() => {
    const badges = {};
    Object.entries(SOCIAL_BADGE_SRC).forEach(([key, src]) => {
      const im = new Image();
      im.src = src;
      badges[key] = im;
    });
    socialBadgesRef.current = badges;
  }, []);

  useEffect(() => {
    const videos = [beforeVideoRef.current, ...finishVideoRefs.map((r) => r.current)].filter(Boolean);
    videos.forEach((v) => v.play().catch(() => {}));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!reactionActiveRef.current) return;
      const frac = afterClipFracRef.current;
      if (frac < 0.02) return;
      reactionsArrRef.current.push({
        emoji: REACTIONS[Math.floor(Math.random() * REACTIONS.length)],
        x: (0.1 + Math.random() * 0.8) * frac,
        fontSize: 18 + Math.random() * 14,
        born: performance.now(),
        duration: 2500 + Math.random() * 2000,
      });
    }, 130);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = masterCanvasRef.current;
    if (!container || !canvas) return;

    let rafId;
    let startTime = null;
    let prevElapsed = null;
    let ro;

    function resetHeroVideosToStart() {
      const videos = [beforeVideoRef.current, ...finishVideoRefs.map((r) => r.current)].filter(Boolean);
      videos.forEach((v) => {
        try {
          v.currentTime = 0;
          v.play().catch(() => {});
        } catch {
          /* ignore */
        }
      });
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w < 1 || h < 1) return;
      const pw = Math.round(w * dpr);
      const ph = Math.round(h * dpr);
      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }
    }

    function render(timestamp) {
      resize();
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const W = container.clientWidth;
      const H = container.clientHeight;
      if (W < 1 || H < 1) {
        rafId = requestAnimationFrame(render);
        return;
      }

      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) % CYCLE;
      if (prevElapsed !== null && elapsed < prevElapsed) {
        resetHeroVideosToStart();
      }
      prevElapsed = elapsed;

      const state = getWipeState(elapsed);
      reactionActiveRef.current = state.reactionActive;

      const splitPx = (state.divX / 100) * W;
      const beforeClipW = (state.beforeLeftPct / 100) * W;
      const afterClipW = (state.afterLeftPct / 100) * W;
      afterClipFracRef.current = W > 0 ? afterClipW / W : 0;

      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#f4f4f0';
      ctx.fillRect(0, 0, W, H);

      const logo = logoRef.current;
      const s = Math.min(W / 900, 1);

      if (state.brandMode === 'full') {
        drawBrand(ctx, W, H, logo, s);
      } else if (state.brandMode === 'right' && splitPx < W - 0.5) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(splitPx, 0, W - splitPx, H);
        ctx.clip();
        drawBrand(ctx, W, H, logo, s);
        ctx.restore();
      }

      const beforeV = beforeVideoRef.current;
      if (beforeClipW > 0.5) {
        const { cardX, cardY, cardW, cardH } = layoutBeforeCard(W, H);
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, beforeClipW, H);
        ctx.clip();
        ctx.save();
        if (
          clipIntersectionCardShadowBand(ctx, beforeClipW, cardX, cardY, cardW, cardH, W, H)
        ) {
          ctx.shadowColor = 'rgba(0,0,0,0.28)';
          ctx.shadowBlur = 46;
          ctx.shadowOffsetY = 12;
          roundedRectPath(ctx, cardX, cardY, cardW, cardH, 20);
          ctx.fillStyle = '#1a1a1a';
          ctx.fill();
        }
        ctx.restore();
        ctx.save();
        roundedRectPath(ctx, cardX, cardY, cardW, cardH, 20);
        ctx.clip();
        drawVideoCoverInRect(ctx, beforeV, cardX, cardY, cardW, cardH, '#1a1a1a');
        ctx.restore();
        drawBeforeChrome(ctx, cardX, cardY, cardW, cardH, s, W);
        ctx.restore();
      }

      /* Tap shockwave — timer from P1 only; divider draws on top later. */
      const genBtnRipple = generateShortsBtnRef.current;
      if (
        genBtnRipple &&
        genBtnRipple.complete &&
        genBtnRipple.naturalWidth > 0 &&
        elapsed >= P1
      ) {
        const layRipple = getGenerateShortsButtonLayout(genBtnRipple, W, H);
        if (layRipple) {
          const { bx, by, dw, dh } = layRipple;
          const tapCx = bx + dw * GEN_BTN_RIPPLE_CENTER_X_FRAC;
          const tapCy = by + dh * GEN_BTN_RIPPLE_CENTER_Y_FRAC;
          const sinceTap = elapsed - P1;

          const u1 = sinceTap / TAP_RIPPLE_DURATION;
          if (u1 >= 0 && u1 < 1) {
            drawTapRippleBackground(ctx, tapCx, tapCy, u1, W, H, 1);
          }

          const u2 = (sinceTap - TAP_ECHO_DELAY_1) / TAP_RIPPLE_DURATION;
          if (u2 >= 0 && u2 < 1) {
            drawTapRippleBackground(ctx, tapCx, tapCy, u2, W, H, 0.5);
          }

          const u3 = (sinceTap - TAP_ECHO_DELAY_2) / TAP_RIPPLE_DURATION;
          if (u3 >= 0 && u3 < 1) {
            drawTapRippleBackground(ctx, tapCx, tapCy, u3, W, H, 0.25);
          }
        }
      }

      const genBtn = generateShortsBtnRef.current;
      if (genBtn && genBtn.complete && genBtn.naturalWidth > 0 && elapsed >= P0 && elapsed < P3) {
        let clipL = 0;
        if (elapsed < P1) clipL = beforeClipW;
        else if (elapsed >= P2) clipL = splitPx;
        const layBtn = getGenerateShortsButtonLayout(genBtn, W, H);
        const handOverlay = getGenerateShortsHandOverlay(genBtn, W, H, elapsed, clipL);
        const btnScale = handOverlay ? handOverlay.buttonScale : 1;
        const btnOpacity = handOverlay ? handOverlay.reveal01 : elapsed >= P2 ? 0 : 1;
        if (btnOpacity > 0.01) {
          drawGenerateShortsButton(ctx, genBtn, W, H, clipL, btnScale, btnOpacity);
        }
        if (handOverlay && handOverlay.reveal01 > 0.02) {
          ctx.save();
          if (clipL > 0.5) {
            ctx.beginPath();
            ctx.rect(clipL, 0, W - clipL, H);
            ctx.clip();
          }
          drawHandCursor(
            ctx,
            handOverlay.tipX,
            handOverlay.tipY,
            handOverlay.cursorScale,
            handOverlay.press01,
            handOverlay.reveal01,
          );
          ctx.restore();
        }
      }

      const now = timestamp;
      reactionsArrRef.current = reactionsArrRef.current.filter(
        (r) => Math.max(0, now - r.born) < r.duration,
      );
      /* Do not clear when reactionActive ends at P4 — SWEEP_2 still runs; clearing
         there removed every emoji while the divider had barely moved. Stop spawning
         via reactionActiveRef; clear leftovers once the after wedge is gone. */
      if (!state.reactionActive && afterClipW <= 0.5) {
        reactionsArrRef.current = [];
      }
      reactionsArrRef.current.forEach((r) => {
        const age = Math.max(0, now - r.born);
        const t = age / r.duration;
        const rx = r.x * W;
        /* Hide past the same right edge as the after wedge — thin divider does not mask them. */
        if (rx > afterClipW) return;
        const ry = H - t * H * 0.9;
        const alpha =
          t < 0.12 ? t / 0.12 : t > 0.8 ? 1 - (t - 0.8) / 0.2 : 0.85;
        const scale =
          t < 0.12 ? t / 0.12 : t > 0.8 ? 1 - (t - 0.8) / 0.2 : 1;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.font = `${r.fontSize * scale}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(r.emoji, rx, ry);
        ctx.restore();
      });

      if (afterClipW > 0.5) {
        const { clips } = layoutAfterClips(W, H, s);
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, afterClipW, H);
        ctx.clip();
        clips.forEach((c, i) => {
          const v = finishVideoRefs[i].current;
          ctx.save();
          if (
            clipIntersectionCardShadowBand(ctx, afterClipW, c.x, c.y, c.w, c.h, W, H)
          ) {
            ctx.shadowColor = 'rgba(0,0,0,0.24)';
            ctx.shadowBlur = 40;
            ctx.shadowOffsetY = 11;
            roundedRectPath(ctx, c.x, c.y, c.w, c.h, c.r);
            ctx.fillStyle = c.fb;
            ctx.fill();
          }
          ctx.restore();
          ctx.save();
          roundedRectPath(ctx, c.x, c.y, c.w, c.h, c.r);
          ctx.clip();
          drawVideoCoverInRect(ctx, v, c.x, c.y, c.w, c.h, c.fb);
          ctx.restore();
          drawClipBadge(ctx, c.x, c.y, c.w, c.h, s, c.views, c.plat, socialBadgesRef.current);
        });
        ctx.restore();
      }

      drawDivider(ctx, splitPx, H);

      rafId = requestAnimationFrame(render);
    }

    ro = new ResizeObserver(() => resize());
    ro.observe(container);
    rafId = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="hero-wipe-root" aria-hidden="true">
      <div className="hw-video-sources">
        <video
          ref={beforeVideoRef}
          className="hw-src-video"
          src={BEFORE_VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
        />
        {FINISH_VIDEO_SRCS.map((src, i) => (
          <video
            key={src}
            ref={finishVideoRefs[i]}
            className="hw-src-video"
            src={src}
            autoPlay
            muted
            loop
            playsInline
          />
        ))}
      </div>

      <div className="wipe-container" ref={containerRef}>
        <canvas ref={masterCanvasRef} className="hw-master-canvas" />
      </div>
    </div>
  );
}
